import { DomainReference, Optional } from '@rygen/rygen-ui/model';

import { ValidShipOrder } from '../ValidShipOrder';

export class PlanningPool {
  shipOrders: ValidShipOrder[] = [];
  loading = false;
  error: unknown;

  constructor(opts?: Partial<PlanningPool>) {
    if (opts?.shipOrders) {
      this.shipOrders = opts.shipOrders.map((so) => new ValidShipOrder(so));
    }

    if (opts?.loading) {
      this.loading = opts.loading;
    }

    if (opts?.error) {
      this.error = opts.error;
    }
  }

  /**
   * The ship orders in the planning pool
   *
   * This property is an alias for the `shipOrders` property and is here so the
   * class fulfills the `Loadable` type.
   **/
  get data(): ValidShipOrder[] {
    return this.shipOrders;
  }

  set data(data: Optional<ValidShipOrder[]>) {
    this.shipOrders = data ?? [];
  }

  get owner(): Optional<DomainReference> {
    return this.shipOrders[0]?.owner;
  }

  reset() {
    this.shipOrders = [];
    this.loading = false;
  }

  addShipOrders(shipOrdersToAdd: ValidShipOrder[]) {
    this.shipOrders.push(...shipOrdersToAdd);
  }

  findShipOrder(shipOrderId: ValidShipOrder['id']): ValidShipOrder {
    const shipOrder = this.shipOrders.find((so) => so.id === shipOrderId);

    if (!shipOrder) {
      throw new Error(
        `Could not find ship order ${shipOrderId} in planning pool`,
      );
    }

    return shipOrder;
  }

  removeShipOrders(shipOrderIds: Array<ValidShipOrder['id']>) {
    this.shipOrders = this.shipOrders.filter(
      (so) => !shipOrderIds.includes(so.id),
    );
  }

  insertNewShipOrdersAfterExistingShipOrder(
    existingShipOrder: ValidShipOrder,
    ...newShipOrders: ValidShipOrder[]
  ) {
    const existingShipOrderIndex = this.shipOrders.findIndex(
      (so) => so.id === existingShipOrder.id,
    );

    const shipOrdersToInsert = [existingShipOrder, ...newShipOrders];
    this.shipOrders.splice(existingShipOrderIndex, 1, ...shipOrdersToInsert);
  }
}
